import { JsonFileDataParser } from '@database/central';
import { WorkspaceDatabaseKeys } from '@database/workspace';
import { TranslateService } from '@ngx-translate/core';
import { AppVariables, ColumnModel, ErrorCodes, FileModel, FusionEntity, ToastDuration, ToastService } from '@shared';
import { LoggingService } from '@shared/logging';
import { IImportHandler } from '../import-handler.interface';

export class FileValidatorHandler implements IImportHandler {
	private next: IImportHandler;

	public constructor(
		private loggingService: LoggingService,
		private jsonFileDataParser: JsonFileDataParser,
		private toastService: ToastService,
		private translateService: TranslateService
	) {}

	public setNext(nextHandler: IImportHandler) {
		this.next = nextHandler;
	}

	public async handle(model: FileModel) {
		await this.checkProjects(model);
		await this.checkEntities(model);
		await this.checkComposedPrimaryKey(model);
		await this.checkdataTables(model);
		await this.checkColumns(model);
		await this.checkColumnsWidth(model);

		if (this.next) {
			await this.next.handle(model);
		}
	}

	private async checkProjects(model: FileModel): Promise<void> {
		try {
			const projects = this.jsonFileDataParser.extractProjectsList(model.FusionReferenceTables);
			
			if (!projects || projects.length === 0) {
				throw new Error('No projects found in the imported file');
			}
		} catch (error) {
			this.loggingService.error(ErrorCodes.FileValidatorHandlerNoProjects, error);
			await this.toastService.presentError(
				this.translateService.instant('alert.import.validationMessages.noProjects'),
				ToastDuration.Slow
			);
			throw error;
		}
	}

	private async checkEntities(model: FileModel) {
		try {
			if (!model.FusionEntities || model.FusionEntities.length === 0) {
				throw new Error('No Entities found in the imported file');
			}
		} catch (error) {
			this.loggingService.error(ErrorCodes.FileValidatorHandlerNoEntities, error);
			await this.toastService.presentError(
				this.translateService.instant('alert.import.validationMessages.noEntities'),
				ToastDuration.Slow
			);
			throw error;
		}
	}

	private async checkComposedPrimaryKey(model: FileModel) {
		try {
			if (model.FusionEntities.some((entity: FusionEntity) => !Array.isArray(entity.primary_key))) {
				throw new Error('Version of JSON file not supported.');
			}
		} catch (error) {
			this.loggingService.error(ErrorCodes.FileValidatorHandlerNoCombinedPrimaryKey, error);
			await this.toastService.presentError(
				this.translateService.instant('alert.import.validationMessages.noCombinedPrimaryKey'),
				ToastDuration.Slow
			);
			throw error;
		}
	}

	private async checkdataTables(model: FileModel) {
		try {
			if (!model.FusionDataTables || model.FusionDataTables.length === 0) {
				throw new Error('No Data Tables found in the imported file');
			}
		} catch (error) {
			this.loggingService.error(ErrorCodes.FileValidatorHandlerNoDataTables, error);
			await this.toastService.presentError(
				this.translateService.instant('alert.import.validationMessages.noDataTables'),
				ToastDuration.Slow
			);
			throw error;
		}
	}

	private async checkColumns(model: FileModel) {
		try {
			if (!model.FusionDataColumns || model.FusionDataColumns.length === 0) {
				throw new Error('No columns found in the imported file');
			}
		} catch (error) {
			this.loggingService.error(ErrorCodes.FileValidatorHandlerNoDataColumns, error);
			await this.toastService.presentError(
				this.translateService.instant('alert.import.validationMessages.noColumns'),
				ToastDuration.Slow
			);
			throw error;
		}
	}

	private async checkColumnsWidth(model: FileModel) {
		try {
			model.FusionDataColumns.filter(
				(column: ColumnModel) =>
					(column.column_name.toLowerCase() === WorkspaceDatabaseKeys.columns.depthFrom ||
						column.column_name.toLowerCase() === WorkspaceDatabaseKeys.columns.depthTo) &&
					column.width > AppVariables.NUMERIC_COLUMN_MAX_WIDTH
			).map((column: ColumnModel) => {
				if (column) {
					const message = this.translateService.instant('alert.import.validationMessages.maxColumnWidth', {
						columnName: column.column_name,
						tableName: column.table_name,
						maxAllowedWidth: AppVariables.NUMERIC_COLUMN_MAX_WIDTH
					});
					throw new Error(message);
				}
			});
		} catch (error) {
			this.loggingService.error(ErrorCodes.FileValidatorHandlerMaxColumnWidth, error);
			await this.toastService.presentError(error, ToastDuration.Slow);
			throw error;
		}
	}
}
